/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/laneMenuItems.ts
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-ITEMS-001
 * PURPOSE: Generate lane/chip context-menu item models from the action registry.
 * ROLE IN ARCHITECTURE: Pure builder between registry contract and LaneContextMenu.
 * MAIN EXPORTS: buildLaneMenuItems, LaneMenuItemModel, LaneMenuDeps.
 * NON-RESPONSIBILITIES: No rendering, no clipboard/network side effects of its own.
 * NOTES FOR FUTURE AI: Items whose transport dep is absent are OMITTED, never
 *   rendered disabled-only (BUG-2026-07-17-EMAIL-LANE-DEAD-CONTEXTMENU).
 *   Lane menus must stay a subset of the owner action's contextActions order.
 */
import { getReaderAction } from '../actions/registry'
import { LANE_MENU_OWNER, type ReaderMenuScope } from './menuScopes'

export interface LaneMenuItemModel {
  /** Registry contextActions id (lane scopes) or chip item id. */
  id: string
  label: string
  danger?: boolean
  /** Synchronous kick — optimistic apply happens inside; menu closes after. */
  run: () => void
}

/** Transports the current reader can actually perform. Absent → item omitted. */
export interface LaneMenuDeps {
  askAi?: (prompt: string) => void
  onOpenCompanyContext?: () => void
  onShowCompanyEmails?: () => void
  canRequestRule?: boolean
  onRequestRule?: () => void
  onOpenSignalsManager?: () => void
  onOpenPlacementManager?: () => void
  copyAiSummary?: () => void
  copyAllSignals?: () => void
  /** Chip transports. */
  verification?: { onCopy: () => void; onReject: () => void } | null
  invoice?: { onCopy: () => void } | null
  onResetCategory?: () => void
  onToggleCategoryHidden?: () => void
  categoryHidden?: boolean
  /** Workflow cell transports (change #269 P5). WorkflowRow builds these from
   *  its cell view models; target-specific items (remove THIS watcher) are
   *  present only when the row captured a right-click target. */
  workflow?: {
    changeOwner?: () => void
    clearOwner?: () => void
    pingOwner?: () => void
    openOwnerProfile?: () => void
    dueToday?: () => void
    dueTomorrow?: () => void
    dueThisWeek?: () => void
    duePickDate?: () => void
    dueClear?: () => void
    copyDate?: () => void
    inviteWatcher?: () => void
    removeWatcher?: () => void
    removeWatcherLabel?: string
    pingWatcher?: () => void
    copyWatcherList?: () => void
  } | null
}

type ItemFactory = (deps: LaneMenuDeps) => LaneMenuItemModel | null

const maybe = (
  id: string,
  label: string,
  run: (() => void) | undefined,
  danger = false,
): LaneMenuItemModel | null => (run ? { id, label, danger, run } : null)

/**
 * Registry contextActions id → concrete item, given available transports.
 * create_personal_label / create_team_label have no imperative transport yet
 * (they live inside the CategoryChip picker) so they resolve to null — the
 * registry keeps declaring them for Phase 5 wiring.
 */
const LANE_ITEM_FACTORIES: Readonly<Record<string, ItemFactory>> = {
  open_company_context: (d) =>
    maybe('open_company_context', 'Open company context', d.onOpenCompanyContext),
  change_relationship: (d) =>
    maybe('change_relationship', 'Add or change relationship', d.onOpenCompanyContext),
  filter_by_company: (d) =>
    maybe('filter_by_company', 'Show emails from this company', d.onShowCompanyEmails),
  ask_ai_identity: (d) =>
    maybe('ask_ai_identity', 'Ask AI why this identity resolved', d.askAi && (() => d.askAi?.('Why was this sender resolved to this company?'))),
  ask_ai_classification: (d) =>
    maybe('ask_ai_classification', 'Ask AI about this classification', d.askAi && (() => d.askAi?.('Why was this email classified this way?'))),
  request_classification_rule: (d) =>
    maybe('request_classification_rule', 'Request a classification rule', d.canRequestRule ? d.onRequestRule : undefined),
  create_personal_label: () => null,
  create_team_label: () => null,
  manage_tags: (d) =>
    maybe('manage_tags', 'Manage tags and signals', d.onOpenSignalsManager),
  add_tag: (d) => maybe('add_tag', 'Add tag…', d.onOpenSignalsManager),
  copy_all_signals: (d) =>
    maybe('copy_all_signals', 'Copy all structured signals', d.copyAllSignals),
  copy_ai_summary: (d) =>
    maybe('copy_ai_summary', 'Copy AI summary', d.copyAiSummary),
  ask_ai_signals: (d) =>
    maybe('ask_ai_signals', 'Ask AI about these signals', d.askAi && (() => d.askAi?.('Explain the signals extracted from this email.'))),
  open_placement_manager: (d) =>
    maybe('open_placement_manager', 'Open Placement Manager', d.onOpenPlacementManager),
  add_placement: (d) =>
    maybe('add_placement', 'Add placement…', d.onOpenPlacementManager),
  link_existing_card: (d) =>
    maybe('link_existing_card', 'Link existing card…', d.onOpenPlacementManager),
  explain_filing: (d) =>
    maybe('explain_filing', 'Explain this filing', d.askAi && (() => d.askAi?.('Why was this email filed here?'))),
  request_filing_rule: (d) =>
    maybe('request_filing_rule', 'Request a filing rule', d.canRequestRule ? d.onRequestRule : undefined),
  // ── Workflow cells (change #269 P5) — subsets of manage_owner/due/watchers.
  change_owner: (d) =>
    maybe('change_owner', 'Change owner…', d.workflow?.changeOwner),
  clear_owner: (d) =>
    maybe('clear_owner', 'Clear owner', d.workflow?.clearOwner, true),
  ping_owner: (d) => maybe('ping_owner', 'Ping owner', d.workflow?.pingOwner),
  open_profile: (d) =>
    maybe('open_profile', 'Open profile', d.workflow?.openOwnerProfile),
  due_today: (d) => maybe('due_today', 'Due today', d.workflow?.dueToday),
  due_tomorrow: (d) =>
    maybe('due_tomorrow', 'Due tomorrow', d.workflow?.dueTomorrow),
  due_this_week: (d) =>
    maybe('due_this_week', 'Due this week', d.workflow?.dueThisWeek),
  due_pick_date: (d) =>
    maybe('due_pick_date', 'Pick date…', d.workflow?.duePickDate),
  due_clear: (d) => maybe('due_clear', 'Clear due date', d.workflow?.dueClear, true),
  copy_date: (d) => maybe('copy_date', 'Copy date', d.workflow?.copyDate),
  invite_watcher: (d) =>
    maybe('invite_watcher', 'Invite team member…', d.workflow?.inviteWatcher),
  remove_watcher: (d) =>
    maybe(
      'remove_watcher',
      d.workflow?.removeWatcherLabel ?? 'Remove watcher',
      d.workflow?.removeWatcher,
      true,
    ),
  ping_watcher: (d) =>
    maybe('ping_watcher', 'Ping watcher', d.workflow?.pingWatcher),
  copy_watcher_list: (d) =>
    maybe('copy_watcher_list', 'Copy watcher list', d.workflow?.copyWatcherList),
}

/** Chip-scoped menus for chips WITHOUT a native menu of their own. */
function buildChipItems(
  chip: string,
  deps: LaneMenuDeps,
): LaneMenuItemModel[] {
  switch (chip) {
    case 'verification':
      return [
        maybe('copy_code', 'Copy code', deps.verification?.onCopy),
        maybe('reject_code', 'Not a code', deps.verification?.onReject, true),
      ].filter((item): item is LaneMenuItemModel => item != null)
    case 'invoice':
      return [
        maybe('copy_invoice', 'Copy invoice number', deps.invoice?.onCopy),
      ].filter((item): item is LaneMenuItemModel => item != null)
    case 'tag':
      return [
        maybe('manage_tags', 'Manage tags and signals', deps.onOpenSignalsManager),
      ].filter((item): item is LaneMenuItemModel => item != null)
    case 'provenance':
      return [
        maybe('ask_ai_signals', 'Explain this signal', deps.askAi && (() => deps.askAi?.('Explain the provenance signals on this email.'))),
      ].filter((item): item is LaneMenuItemModel => item != null)
    case 'category':
      return [
        maybe('reset_to_ai', 'Reset to AI suggestion', deps.onResetCategory),
        maybe(
          'toggle_category_hidden',
          deps.categoryHidden ? 'Show this category in inbox' : 'Hide this category from inbox',
          deps.onToggleCategoryHidden,
        ),
      ].filter((item): item is LaneMenuItemModel => item != null)
    default:
      return []
  }
}

/**
 * Build the menu items for a routed scope.
 *
 * Inputs: the resolved scope and the transports the host actually wired.
 * Output: ordered item models; lane scopes preserve the registry's
 * contextActions order and never invent ids outside it.
 * Failure mode: unknown ids resolve to null and are skipped (loud in tests,
 * silent at runtime — menu simply omits the row).
 */
export function buildLaneMenuItems(
  scope: ReaderMenuScope,
  deps: LaneMenuDeps,
): LaneMenuItemModel[] {
  if (scope.kind === 'chip') {
    const items = buildChipItems(scope.chip, deps)
    // Chip with nothing actionable falls back to its bare lane menu so the
    // user never sees an empty panel.
    if (items.length > 0) return items
  }
  const owner = getReaderAction(LANE_MENU_OWNER[scope.lane])
  return owner.contextActions
    .map((id) => LANE_ITEM_FACTORIES[id]?.(deps) ?? null)
    .filter((item): item is LaneMenuItemModel => item != null)
}
